import { TransportType } from '#/shared/api/generated/shipment/models'
import type { ShipmentListItem } from '#/shared/api/generated/shipment/models'
import { splitTimestamp } from '#/shared/lib/format'
import type { IdCellBadge } from '#/shared/ui/table-cells'
import type { ShipmentRow } from '../model/shipment-row'
import type { ShipmentStage } from '../model/stages'
import {
  describeRoute,
  describeVehicles,
  formatCustomer,
  formatOwnerName,
  formatShipmentStatus,
  parseTariff,
} from './format'

const DUPLICATE_BADGE: IdCellBadge = { label: 'P.D.', variant: 'warning' }

/**
 * Flattens a `ShipmentListItem` from the API into the shared `ShipmentRow`
 * every list table (leads, quotes, orders) consumes. Fields the API doesn't
 * expose today (noteCount, vehicle flags, owner avatar) stay undefined so
 * the cells fall back to their empty rendering.
 */
export function shipmentToRow(item: ShipmentListItem): ShipmentRow {
  const created = splitTimestamp(item.created_at)
  const customer = formatCustomer(item.customer)
  const vehicle = describeVehicles(item.vehicles)
  const route = describeRoute(item.stops)
  const shipDate = item.first_available_date
    ? splitTimestamp(item.first_available_date).date
    : ''

  const providerBadges: IdCellBadge[] = []
  if (item.has_duplicate) providerBadges.push(DUPLICATE_BADGE)
  if (item.provider) providerBadges.push({ label: item.provider.name })
  const badges = providerBadges.length > 0 ? providerBadges : undefined

  return {
    id: item.id,
    shipmentId: item.id,
    code: item.code,
    badges,
    providerBadges: badges,
    statusLabel: formatShipmentStatus(item.status) || undefined,
    status: item.status,
    // `stage` is returned by the API but not yet in the generated ShipmentListItem
    // type — read it through a cast until the schema is regenerated (cf. the
    // `estimated_delivery_at` cast in order-row.ts).
    stage: (item as { stage?: ShipmentStage }).stage,
    createdDate: created.date,
    createdTime: created.time,
    noteCount: item.notes_count,
    ownerName: formatOwnerName(item.user),
    customerName: customer.name,
    customerPhone: customer.phone,
    customerEmail: customer.email,
    customerId: customer.id,
    customerDeliverability: customer.deliverability,
    vehicleName: vehicle.name,
    vehicleExtraCount: vehicle.extraCount,
    vehicleHasEngine: vehicle.isInoperable === true,
    vehicleIsEnclosed: item.transport_type === TransportType.ENCLOSED,
    origin: route.origin,
    destination: route.destination,
    tariff: parseTariff(item.tariff),
    deposit: parseTariff(item.deposit),
    shipDate,
  }
}
